import { inject, Injectable } from '@angular/core';
import { ApiGateway, ArrayStrategy, objectToSearchParams } from '@sb/shared/api';
import type { PlainDateRange } from '@shiftbase-com/ts-shared/utilities';
import { forkJoin, map, of } from 'rxjs';

import { splitIntoPriorityWindows } from './priority-date-windows';
import { normalizePriorityEntry } from './timesheet-priority.helper';
import type { PriorityEntry, PriorityTimesheetResponse, PriorityTimesheetsRequest } from './timesheet-priority.models';

@Injectable({ providedIn: 'root' })
export class TimesheetPriorityApiService {
  #gateway = inject(ApiGateway);
  #endpoint = 'timesheets/priority';

  list(request: PriorityTimesheetsRequest) {
    return this.#gateway
      .get<PriorityTimesheetResponse[]>(this.#endpoint, {
        params: objectToSearchParams(request, ArrayStrategy.COMMA_SEPARATED),
        headers: { ignoreMetaMessage: true },
      })
      .pipe(
        map((responses) =>
          responses.map(normalizePriorityEntry).filter((entry): entry is PriorityEntry => entry !== null),
        ),
      );
  }

  /** Covers a range of any length, which `list` cannot — the endpoint caps a single request. */
  listForRange(range: PlainDateRange, departmentIds: string[]) {
    const windows = splitIntoPriorityWindows(range);

    if (windows.length === 0) {
      return of<PriorityEntry[]>([]);
    }

    return forkJoin(
      windows.map((window) => this.list({ startDate: window.start, endDate: window.end, departmentIds })),
    ).pipe(map((windowEntries) => windowEntries.flat()));
  }
}
